import React, { useEffect, useState } from "react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { useCurrency } from "@/currency";
import { useAuth, can } from "@/auth";
import { PageHeader, Tile, Loading } from "@/components/Bits";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";

const SUB_SYSTEMS = [
  "Air Compressor", "Air Dryer", "Brake Control Unit", "Brake Disk", "Brake System",
  "Coupler (Automatic)", "Coupler (Hook)", "Coupler (Semi)", "Deadman", "Door Panels",
  "Elastomers", "Event Recorder", "Ground Contact", "HVAC Cabin", "HVAC Saloon",
  "Master Controller", "PIS", "Pantograph", "Sanding System", "Speed Sensor",
  "Third Rail", "Tread Brake Unit",
];

const EMPTY = { date: new Date().toISOString().slice(0, 10), sub_system: "Air Compressor", price: "", currency: "EUR", source: "Catalog", supplier: "", notes: "" };

export default function Prices() {
  const { t } = useI18n();
  const { fmt } = useCurrency();
  const { user } = useAuth();
  const editable = can(user, "admin");
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/prices").then((r) => setRows(r.data.rows));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({
      date: (r.date || "").slice(0, 10),
      sub_system: r.sub_system,
      price: r.price,
      currency: r.currency || "EUR",
      source: r.source || "",
      supplier: r.supplier || "",
      notes: r.notes || "",
    });
    setEditingId(r.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.price) return;
    const body = { ...form, price: parseFloat(form.price) };
    if (editingId) await api.put(`/prices/${editingId}`, body);
    else await api.post("/prices", body);
    setOpen(false); setForm(EMPTY); setEditingId(null);
    await load();
  };

  const del = async (id) => {
    if (!confirm("Delete this price entry?")) return;
    await api.delete(`/prices/${id}`); await load();
  };

  if (!rows) return <Loading />;

  return (
    <div data-testid="prices-page">
      <PageHeader
        title={t("prices.title")}
        subtitle={t("prices.subtitle")}
        right={
          editable && (
            <button onClick={openNew} className="btn-accent flex items-center gap-2" data-testid="price-new">
              <Plus size={14} weight="bold" /> {t("prices.newBtn")}
            </button>
          )
        }
      />
      <div className="p-4 md:p-10">
        <Tile>
          <div className="overflow-x-auto">
          <table className="tech" data-testid="prices-table">
            <thead>
              <tr>
                <th>{t("prices.date")}</th>
                <th>{t("table.sub")}</th>
                <th className="text-right">{t("prices.price")}</th>
                <th>{t("prices.currency")}</th>
                <th>{t("table.supplier")}</th>
                <th>{t("prices.source")}</th>
                <th>{t("prices.notes")}</th>
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={editable ? 8 : 7} className="text-center py-16">
                    <div className="label-tech" style={{ color: "var(--text-tertiary)" }}>No prices yet</div>
                    <div className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>Opportunities use catalog fallback prices.</div>
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="font-mono text-[12px]">{(r.date || "").slice(0, 10)}</td>
                  <td>{r.sub_system}</td>
                  <td className="num">{fmt(r.currency === "EUR" ? r.price : r.price / 1)} <span className="opacity-50 text-[11px]">({r.currency})</span></td>
                  <td className="font-mono text-[12px]">{r.currency}</td>
                  <td>{r.supplier}</td>
                  <td className="text-[12px]">{r.source}</td>
                  <td className="text-[12px]" style={{ color: "var(--text-secondary)" }}>{r.notes}</td>
                  {editable && (
                    <td className="text-right">
                      <button onClick={() => openEdit(r)} className="p-1.5 rounded-full hover:bg-[var(--bg-subtle)]" data-testid={`edit-${r.id}`}>
                        <PencilSimple size={14} />
                      </button>
                      <button onClick={() => del(r.id)} className="p-1.5 rounded-full hover:bg-[var(--bg-subtle)]" data-testid={`del-${r.id}`}>
                        <Trash size={14} style={{ color: "var(--accent)" }} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Tile>
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)} title={editingId ? "Edit price" : "New price"} testId="price-modal">
          <div className="grid grid-cols-2 gap-3">
            <FieldText label={t("prices.date")} type="date" v={form.date} set={(v) => setForm({ ...form, date: v })} />
            <FieldSel label={t("prices.currency")} v={form.currency} set={(v) => setForm({ ...form, currency: v })} opts={["EUR", "USD", "TRY"]} />
            <FieldSel colspan label={t("table.sub")} v={form.sub_system} set={(v) => setForm({ ...form, sub_system: v })} opts={SUB_SYSTEMS} />
            <FieldText label={t("prices.price")} type="number" v={form.price} set={(v) => setForm({ ...form, price: v })} testId="price-input" />
            <FieldText label={t("table.supplier")} v={form.supplier} set={(v) => setForm({ ...form, supplier: v })} />
            <FieldText colspan label={t("prices.source")} v={form.source} set={(v) => setForm({ ...form, source: v })} />
            <div className="col-span-2">
              <label className="label-tech block mb-1.5">{t("prices.notes")}</label>
              <textarea rows={2} className="input-tech" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="btn-ghost" data-testid="price-cancel">{t("visits.cancel")}</button>
            <button onClick={save} className="btn-primary" data-testid="price-save">{t("visits.save")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <div className="tile p-7 w-full max-w-md fade-in" onClick={(e) => e.stopPropagation()} data-testid={testId} style={{ boxShadow: "var(--shadow-lg)" }}>
        <h3 className="text-[20px] font-semibold mb-5 tracking-tight">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function FieldText({ label, v, set, type = "text", colspan, testId }) {
  return (
    <div className={colspan ? "col-span-2" : ""}>
      <label className="label-tech block mb-1.5">{label}</label>
      <input type={type} className="input-tech" value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={testId} />
    </div>
  );
}

function FieldSel({ label, v, set, opts, colspan }) {
  return (
    <div className={colspan ? "col-span-2" : ""}>
      <label className="label-tech block mb-1.5">{label}</label>
      <select className="input-tech" value={v || ""} onChange={(e) => set(e.target.value)}>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
